import { createEntityAdapter } from "@reduxjs/toolkit";

import apiSlice from "../api/apiSlice";

const loadsAdapter = createEntityAdapter({});

const initialState = loadsAdapter.getInitialState({});

const LOADS_ANALYTICS_TAGS = [
  { type: "Metrics", id: "Loads-Kpis" },
  { type: "Metrics", id: "Loads-Community" },
  { type: "Metrics", id: "Loads-Meters" },
];

export const loadsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getLoadsByCommunityId: builder.query({
      query: (communityId) => ({
        url: `/communities/${communityId}/loads`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      transformResponse: (responseData) => {
        if (responseData) {
          return loadsAdapter.setAll(initialState, responseData);
        }
        return [];
      },
      providesTags: (result) => {
        if (result?.ids) {
          return [{ type: "Load", id: "LIST" }, ...result.ids.map((id) => ({ type: "Load", id }))];
        }
        return [{ type: "Load", id: "LIST" }];
      },
    }),
    getLoadsByCommunityIdPagination: builder.query({
      query: ({
        limit,
        page,
        date,
        time,
        cpe,
        meterType,
        communityId,
        sortColumn,
        sortDirection,
      }) => ({
        url:
          `/communities/${communityId}/loads/pagination?` +
          `limit=${limit}&` +
          `offset=${page * limit}` +
          `${date ? `&date=${date}` : ""}` +
          `${time ? `&time=${time}` : ""}` +
          `${cpe ? `&meter_id=${cpe}` : ""}` +
          `${meterType ? `&meter_type=${meterType}` : ""}` +
          `${sortDirection ? `&sort_direction=${sortDirection}` : ""}` +
          `${sortColumn ? `&sort_column=${sortColumn}` : ""}`,
        method: "GET",
      }),
      providesTags: () => [{ type: "Load", id: "LIST" }],
    }),
    getLoadsKpisByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/loads/metrics/kpis?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Loads-Kpis" }],
    }),
    getAggregatedLoadsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/loads?` +
          `granularity=${granularity}&` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Loads-Community" }],
    }),
    getMetersLoadsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/loads/loadsbymeter?` +
          `granularity=${granularity}&` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Loads-Meters" }],
    }),
    getPVGisGeneration: builder.query({
      query: ({ latitude, longitude, startYear, endYear, startDate, endDate }) => ({
        url: `/loads/pvgis_generation`,
        method: "GET",
        params: {
          latitude,
          longitude,
          start_year: startYear,
          end_year: endYear,
          start_date: startDate,
          end_date: endDate,
        },
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    /* POST */
    addNewLoads: builder.mutation({
      query: ({ file, communityId }) => {
        const formData = new FormData();

        formData.append("file_upload", file, file.name);

        return {
          url: `/communities/${communityId}/loads/file_upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "Load", id: "LIST" }],
    }),
    /* DELETE */
    deleteLoads: builder.mutation({
      query: ({ communityId, startDate, endDate }) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        const qs = params.toString();
        return { url: `/communities/${communityId}/loads${qs ? `?${qs}` : ""}`, method: "DELETE" };
      },
      invalidatesTags: [{ type: "Load", id: "LIST" }, ...LOADS_ANALYTICS_TAGS],
    }),
  }),
});

export const {
  useGetLoadsByCommunityIdQuery,
  useGetLoadsByCommunityIdPaginationQuery,
  useAddNewLoadsMutation,
  useDeleteLoadsMutation,
  useGetLoadsKpisByCommunityIdQuery,
  useGetAggregatedLoadsByCommunityIdQuery,
  useGetMetersLoadsByCommunityIdQuery,
  useGetAggregatedMetersLoadsByCommunityIdQuery,
  useGetPVGisGenerationQuery,
} = loadsApiSlice;
